import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, LabelList, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, HandCoins, Percent, TrendingUp, UserCheck, Users, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Select } from '@/components/form/Select';
import { getMecaReport, type MecaMonth, type MecaReport } from '@/api/reports';
import { formatINR } from '@/lib/format';
import { apiError } from '@/lib/apiError';

// MeCA — Meet · Convert · Grow. Monthly lead-to-revenue performance for one quarter + the quarter average.
// Built from the standard ERP pieces: KPI cards, filter bar, shared table, chart cards.

const QUARTERS = [
  { q: 1, label: 'Q1', months: 'Apr – Jun' },
  { q: 2, label: 'Q2', months: 'Jul – Sep' },
  { q: 3, label: 'Q3', months: 'Oct – Dec' },
  { q: 4, label: 'Q4', months: 'Jan – Mar' },
];

/** Financial year + quarter for today (India time). FY 2026 = Apr 2026 – Mar 2027. */
function currentFyQuarter() {
  const now = new Date(Date.now() + 330 * 60_000);
  const m = now.getUTCMonth();
  return { fy: m >= 3 ? now.getUTCFullYear() : now.getUTCFullYear() - 1, quarter: Math.floor(((m + 9) % 12) / 3) + 1 };
}
const fyLabel = (fy: number) => `FY ${fy}–${String(fy + 1).slice(2)}`;

/** 0.5% / 14% — one decimal below 10% so small rates don't all read 0% or 1%. */
const pct = (n: number) => `${n < 10 && !Number.isInteger(n) ? +n.toFixed(1) : Math.round(n)}%`;
const rupees = (n: number) => `₹${formatINR(Math.round(n))}`;
/** ₹6.25L / ₹45.2K — chart labels and axis. */
const shortRs = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (a >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

type Key = 'meetings' | 'conversionPct' | 'clients' | 'avgSale' | 'revenue';
interface Metric {
  key: Key;
  label: string;
  Icon: LucideIcon;
  /** ERP KPI accent — the same colour on the KPI card, the table icon and the chart. */
  color: string;
  fmt: (n: number) => string;
  short: (n: number) => string;
  chart: 'bar' | 'line';
  /** Shown in place of the chart when the quarter has nothing yet. */
  empty: string;
}
const METRICS: Metric[] = [
  { key: 'meetings', label: 'Leads Captured in the Month', Icon: Users, color: '#5b8def', fmt: (n) => String(Math.round(n)), short: (n) => String(Math.round(n)), chart: 'bar', empty: 'No leads captured yet this quarter' },
  { key: 'conversionPct', label: 'Conversion Ratio', Icon: Percent, color: '#10b981', fmt: pct, short: pct, chart: 'line', empty: 'No conversions yet this quarter' },
  { key: 'clients', label: 'Clients', Icon: UserCheck, color: '#8b5cf6', fmt: (n) => String(Math.round(n)), short: (n) => String(Math.round(n)), chart: 'bar', empty: 'No leads converted yet this quarter' },
  { key: 'avgSale', label: 'Average Rs Sale', Icon: HandCoins, color: '#f59e0b', fmt: rupees, short: shortRs, chart: 'bar', empty: 'No sales to converted leads yet this quarter' },
  { key: 'revenue', label: 'Revenue', Icon: TrendingUp, color: '#FF8C00', fmt: rupees, short: shortRs, chart: 'bar', empty: 'No invoices to converted leads yet this quarter' },
];

const cell = (m: Metric, v: number | null | undefined, future: boolean) => (future ? '' : v == null ? '—' : m.fmt(v));

/** The quarter so far as a funnel: meetings → clients (conversion) → revenue. Sits in the filter bar. */
function QuarterFunnel({ data }: { data: MecaReport }) {
  const done = data.months.filter((m) => !m.future);
  const meetings = done.reduce((s, m) => s + m.meetings, 0);
  const clients = done.reduce((s, m) => s + m.clients, 0);
  const revenue = done.reduce((s, m) => s + m.revenue, 0);
  const steps = [
    { label: 'Leads captured', value: String(meetings), color: METRICS[0].color },
    { label: `Clients · ${meetings ? pct((clients / meetings) * 100) : '0%'} converted`, value: String(clients), color: METRICS[2].color },
    { label: 'Revenue', value: shortRs(revenue), color: METRICS[4].color },
  ];
  return (
    <div className="col-span-3 flex flex-wrap items-center gap-x-2.5 gap-y-1 self-center rounded-lg bg-[var(--bg-soft)] px-3 py-1 sm:col-auto sm:ml-auto" aria-label="Quarter so far">
      {steps.map((st, i) => (
        <div key={st.label} className="flex items-center gap-2">
          {i > 0 && <ArrowRight size={14} className="text-[var(--text-muted)]" />}
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-extrabold tabular-nums" style={{ color: st.color }}>{st.value}</span>
            <span className="text-[10px] font-semibold text-[var(--text-muted)]">{st.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function MecaReportPage() {
  const cur = currentFyQuarter();
  const [fy, setFy] = useState(cur.fy);
  const [quarter, setQuarter] = useState(cur.quarter);
  const q = useQuery({ queryKey: ['meca-report', fy, quarter], queryFn: () => getMecaReport(fy, quarter), placeholderData: (p) => p });
  const data = q.data;
  const qInfo = QUARTERS[quarter - 1];
  const fys = Array.from({ length: 4 }, (_, i) => cur.fy - i);

  return (
    // Fills the screen: the charts row takes whatever height is left, so there is no empty band below.
    <div className="flex min-h-full flex-col pb-3">
      <SectionHeader title="MeCA Report" subtitle="Meet · Convert · Grow — monthly performance" />

      <FilterBar>
        <FilterField label="Financial Year">
          <Select value={fy} onChange={(e) => setFy(Number(e.target.value))} aria-label="Financial year">
            {fys.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Quarter" className="col-span-2 sm:col-auto">
          {/* Same pills as the ERP period picker */}
          <div className="flex w-full rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-0.5 sm:inline-flex sm:w-auto" role="group" aria-label="Quarter">
            {QUARTERS.map((x) => (
              <button
                key={x.q}
                type="button"
                onClick={() => setQuarter(x.q)}
                aria-pressed={quarter === x.q}
                className={`min-h-0 flex-1 rounded-md px-3 py-1 text-[11px] font-bold transition sm:flex-none ${quarter === x.q ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-secondary)] hover:text-accent'}`}
                title={x.months}
              >
                {x.label}
              </button>
            ))}
          </div>
        </FilterField>
        {data && <QuarterFunnel data={data} />}
      </FilterBar>

      {q.isError && <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-6 text-center text-sm text-danger">{apiError(q.error, 'Could not load the report')}</div>}
      {data && <MecaBody data={data} quarterLabel={qInfo.label} period={`${qInfo.label} ${fyLabel(fy)} · ${qInfo.months}`} />}
    </div>
  );
}

function MecaBody({ data, quarterLabel, period }: { data: MecaReport; quarterLabel: string; period: string }) {
  const months = data.months;
  return (
    <>
      {/* Metric × month grid in the shared ERP table (phone: one card per metric).
          shrink-0: never squeezed by the full-height column, so it never scrolls inside itself. */}
      <div className="shrink-0">
      <Table<Metric>
        title="Monthly Performance"
        count={period}
        rows={METRICS}
        rowKey={(mt) => mt.key}
        columns={[
          {
            key: 'metric',
            header: 'Metric',
            minWidth: '280px',
            cell: (mt) => (
              // Comfortable rows (≈ 48px) so the table and the charts share the screen evenly
              <div className="flex items-center gap-3 sm:py-1.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg sm:h-8 sm:w-8" style={{ background: `${mt.color}1f`, color: mt.color }}>
                  <mt.Icon size={16} />
                </span>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-[var(--text-primary)] sm:text-sm">{mt.label}</div>
                </div>
              </div>
            ),
          },
          ...months.map((m) => ({
            key: m.key,
            header: m.future ? `${m.label} ${m.year} (upcoming)` : `${m.label} ${m.year}`,
            align: 'center' as const,
            cell: (mt: Metric) => <span className="tabular-nums font-semibold text-[var(--text-primary)] sm:text-base">{cell(mt, m[mt.key], m.future)}</span>,
          })),
          {
            key: 'avg',
            header: `${quarterLabel} Avg`,
            align: 'center' as const,
            cell: (mt: Metric) => <span className="tabular-nums font-extrabold text-accent sm:text-base">{cell(mt, data.average[mt.key], false)}</span>,
          },
        ]}
      />
      </div>

      {/* One small chart per metric */}
      <div className="mt-3 grid flex-1 auto-rows-[minmax(13rem,1fr)] grid-cols-1 gap-3 sm:grid-cols-6 xl:grid-cols-5">
        {METRICS.map((mt, i) => (
          // sm–lg: two wide charts, then three; xl: all five in a row
          <div key={mt.key} className={`${i < 2 ? 'sm:col-span-3' : 'sm:col-span-2'} xl:col-span-1`}>
            <MiniChart metric={mt} months={months} average={data.average[mt.key]} />
          </div>
        ))}
      </div>
    </>
  );
}

const TICK = { fontSize: 10, fill: 'var(--text-muted)' };
const GRID = { strokeDasharray: '3 3', stroke: 'var(--border-color)', vertical: false } as const;

function MiniChart({ metric, months, average }: { metric: Metric; months: MecaMonth[]; average: number | null }) {
  const rows = months.map((m) => ({ name: m.label, value: m.future ? null : m[metric.key] }));
  const empty = rows.every((r) => !r.value);
  const tip = (
    <Tooltip
      cursor={{ fill: 'var(--bg-soft)' }}
      content={({ active, payload, label }) =>
        active && payload?.length && payload[0].value != null ? (
          <div className="rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2 text-[11px] shadow-lg">
            <div className="font-bold text-[var(--text-primary)]">{label}</div>
            <div className="text-[var(--text-secondary)]">{metric.label}: <b>{metric.fmt(Number(payload[0].value))}</b></div>
          </div>
        ) : null
      }
    />
  );
  const axis = (
    <>
      <CartesianGrid {...GRID} />
      <XAxis dataKey="name" tick={TICK} axisLine={false} tickLine={false} padding={metric.chart === 'line' ? { left: 14, right: 14 } : undefined} />
      <YAxis tick={TICK} axisLine={false} tickLine={false} width={46} tickFormatter={(v: number) => metric.short(v)} allowDecimals={metric.key !== 'meetings' && metric.key !== 'clients'} />
    </>
  );
  return (
    <div className="flex h-full flex-col rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-3">
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-bold text-[var(--text-primary)]">
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: metric.color }} />
          {metric.label}
        </div>
        {average != null && (
          <div className="shrink-0 text-right leading-tight">
            <div className="text-sm font-extrabold tabular-nums text-[var(--text-primary)]">{metric.short(average)}</div>
            <div className="text-[9px] font-semibold uppercase text-[var(--text-muted)]">avg</div>
          </div>
        )}
      </div>
      <div className="relative min-h-40 flex-1">
        <div className="absolute inset-0">
        {empty ? (
          <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-[var(--border-color)] text-center">
            <metric.Icon size={18} className="text-[var(--text-muted)]" />
            <div className="mt-1 px-2 text-[11px] font-semibold text-[var(--text-muted)]">{metric.empty}</div>
          </div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          {metric.chart === 'line' ? (
            <LineChart data={rows} margin={{ top: 18, right: 16, left: 0, bottom: 0 }}>
              {axis}
              {tip}
              <Line type="linear" dataKey="value" stroke={metric.color} strokeWidth={2} dot={{ r: 4, fill: metric.color, stroke: 'var(--card-bg)', strokeWidth: 2 }} connectNulls={false} isAnimationActive={false}>
                <LabelList dataKey="value" position="top" fontSize={10} fill="var(--text-primary)" formatter={(v: unknown) => (v == null ? '' : metric.short(Number(v)))} />
              </Line>
            </LineChart>
          ) : (
            <BarChart data={rows} margin={{ top: 18, right: 4, left: 0, bottom: 0 }} barCategoryGap="28%">
              {axis}
              {tip}
              <Bar dataKey="value" fill={metric.color} radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false}>
                <LabelList dataKey="value" position="top" fontSize={10} fill="var(--text-primary)" formatter={(v: unknown) => (v == null ? '' : metric.short(Number(v)))} />
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
        )}
        </div>
      </div>
    </div>
  );
}
